/* eslint-disable react-hooks/exhaustive-deps */
import { FC, memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { WC_NAME } from '../../../utils';
import styles from './styles.module.css';
import type { Props } from './types';

type OverlayProps = Required<
  Pick<
    Props,
    | 'isOpen'
    | 'backgroundColor'
    | 'children'
    | 'closeOnOverlayClick'
    | 'onClose'
  >
>;

const bodyScroll = {
  show: () => (document.body.style.overflow = 'initial'),
  hide: () => (document.body.style.overflow = 'hidden'),
};

export const ModalOverlayComponent: FC<OverlayProps> = ({
  isOpen,
  backgroundColor,
  children,
  closeOnOverlayClick,
  onClose,
}) => {
  const [showPortal, setShowPortal] = useState<boolean>(isOpen);
  const [focusReturnNode, setFocusReturnNode] = useState<
    HTMLElement | Element | null
  >();

  useEffect(() => {
    if (isOpen) {
      setFocusReturnNode(document.activeElement);
      return;
    }

    if (
      focusReturnNode instanceof HTMLElement &&
      document.contains(focusReturnNode)
    ) {
      focusReturnNode?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setShowPortal(false);
        bodyScroll.show();
      }, 400);

      return;
    }

    setShowPortal(isOpen);
    bodyScroll.hide();
  }, [isOpen]);

  useEffect(() => {
    return () => {
      bodyScroll.show();
    };
  }, []);

  const handleAnimationEnd = () => {
    if (isOpen) return;
    setShowPortal(false);
  };

  // TODO: Esto tenemos que revisarlo si queremos tener multiples
  // instancias del reproductor en la misma página
  const containerPortal =
    document.querySelector(WC_NAME)?.shadowRoot! ||
    (document.getElementById('encapsulate-liveshopping') as HTMLIFrameElement)
      .contentDocument?.body ||
    document;

  return showPortal
    ? createPortal(
        <div
          className={`${styles.modalOverlay} ${
            isOpen ? styles.openAnimation : styles.closeAnimation
          } ${styles.zIndex1300}`}
          style={{ backgroundColor }}
          id="custom-modal-overlay"
          tabIndex={-1}
          onClick={e => {
            e.stopPropagation();
            if (!closeOnOverlayClick) return;
            onClose();
          }}
          onAnimationEnd={handleAnimationEnd}
          data-testid="modal__overlay"
          role="presentation"
        >
          {children}
        </div>,
        containerPortal,
      )
    : null;
};

export const ModalOverlay = memo(ModalOverlayComponent);
